import React, { useMemo, useRef, useState } from "react";
import {
  Archive,
  ArrowUpDown,
  Boxes,
  GripVertical,
  Layers,
  Pause,
  Percent,
  Play,
  Plus,
  Search,
  Tag,
  Users,
} from "lucide-react";
import {
  Badge,
  Btn,
  Card,
  Input,
  Select,
} from "../../loyalty/components/primitives";

const TYPE_META = {
  free_slab:    { label: "Free Slab",         tone: "gold",   Icon: Layers },
  disc_material:{ label: "Material Discount", tone: "blue",   Icon: Tag },
  disc_bill_pct:{ label: "Bill %",            tone: "green",  Icon: Percent },
  disc_bill_amt:{ label: "Bill Flat ₹",       tone: "teal",   Icon: Percent },
  combo:        { label: "Combo",             tone: "purple", Icon: Boxes },
  employee:     { label: "Employee",          tone: "amber",  Icon: Users },
};

const STATUS_TONE = {
  active: "green",
  paused: "amber",
  scheduled: "blue",
  inactive: "slate",
  draft: "slate",
  archived: "slate",
};

const fmtDate = (ms) => {
  if (!ms) return "—";
  try {
    return new Date(Number(ms)).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const ListTab = ({ schemes = [], loading, readOnly, onReorder, onStatusChange, onEdit, onCreate }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortKey, setSortKey] = useState("priority");
  const [sortDir, setSortDir] = useState("desc");

  // dragstate
  const dragId = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schemes
      .filter((s) => statusFilter === "all" || s.status === statusFilter)
      .filter((s) => typeFilter === "all" || s.type === typeFilter)
      .filter((s) => !q || (s.name || "").toLowerCase().includes(q));
  }, [schemes, search, statusFilter, typeFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av || "").localeCompare(String(bv || "")) * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  // Re-order only meaningful when the active sort is priority desc — anything
  // else gets a hint and disables drag.
  const dragEnabled =
    !readOnly &&
    sortKey === "priority" &&
    sortDir === "desc" &&
    statusFilter === "all" &&
    typeFilter === "all" &&
    !search.trim();

  const onDragStart = (e, id) => {
    if (!dragEnabled) return;
    dragId.current = id;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, id) => {
    if (!dragEnabled) return;
    e.preventDefault();
    setDragOverId(id);
  };
  const onDrop = (e, targetId) => {
    if (!dragEnabled) return;
    e.preventDefault();
    const sourceId = dragId.current;
    setDragOverId(null);
    dragId.current = null;
    if (!sourceId || sourceId === targetId) return;
    const ids = sorted.map((s) => s.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder?.(next);
  };

  const headerSort = (k, label) => {
    const active = sortKey === k;
    return (
      <button
        type="button"
        onClick={() => {
          if (active) setSortDir(sortDir === "asc" ? "desc" : "asc");
          else {
            setSortKey(k);
            setSortDir(k === "priority" ? "desc" : "asc");
          }
        }}
        className={`inline-flex items-center gap-1 ${
          active ? "text-[#0F172A]" : "text-[#64748B] hover:text-[#334155]"
        }`}
      >
        {label}
        <ArrowUpDown size={12} className={active ? "" : "opacity-50"} />
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-[17px] font-bold text-[#0F172A]">Schemes</h2>
          <p className="text-[13px] text-[#64748B] mt-1 max-w-[720px]">
            {dragEnabled
              ? "Drag rows by the handle to re-rank — higher rows win at billing."
              : "Clear filters and sort by priority desc to enable drag-to-rank."}
          </p>
        </div>
        {!readOnly && onCreate && (
          <Btn variant="primary" onClick={onCreate}>
            <Plus size={14} /> New scheme
          </Btn>
        )}
      </div>

      <Card className="p-3 flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]"
          />
          <Input
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 30 }}
          />
        </div>
        <div style={{ width: 170 }}>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="scheduled">Scheduled</option>
            <option value="paused">Paused</option>
            <option value="inactive">Inactive</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </Select>
        </div>
        <div style={{ width: 200 }}>
          <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All types</option>
            {Object.entries(TYPE_META).map(([k, m]) => (
              <option key={k} value={k}>
                {m.label}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead className="bg-[#F8FAFC] text-[11px] font-bold text-[#64748B] uppercase tracking-wider">
              <tr>
                <th className="w-8 px-3 py-2.5" />
                <th className="px-3 py-2.5 text-left">{headerSort("name", "Name")}</th>
                <th className="px-3 py-2.5 text-left">{headerSort("type", "Type")}</th>
                <th className="px-3 py-2.5 text-left">{headerSort("status", "Status")}</th>
                <th className="px-3 py-2.5 text-right">
                  {headerSort("priority", "Priority")}
                </th>
                <th className="px-3 py-2.5 text-left">Validity</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-[#64748B]">
                    Loading…
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-[#64748B]">
                    No schemes match the current filters.
                  </td>
                </tr>
              ) : (
                sorted.map((s) => {
                  const meta = TYPE_META[s.type] || {
                    label: s.type,
                    tone: "slate",
                    Icon: Tag,
                  };
                  const Icon = meta.Icon;
                  const isOver = dragOverId === s.id;
                  return (
                    <tr
                      key={s.id}
                      onDragOver={(e) => onDragOver(e, s.id)}
                      onDrop={(e) => onDrop(e, s.id)}
                      onDragLeave={() => setDragOverId(null)}
                      className={`border-t border-[#EEF1F5] hover:bg-[#FAFBFC] ${
                        isOver ? "bg-[#EEF2FF]" : ""
                      }`}
                    >
                      <td className="px-3 py-2.5">
                        <span
                          draggable={dragEnabled}
                          onDragStart={(e) => onDragStart(e, s.id)}
                          className={`inline-flex items-center justify-center w-5 h-5 rounded ${
                            dragEnabled
                              ? "text-[#94A3B8] hover:text-[#475569] cursor-grab active:cursor-grabbing"
                              : "text-[#CBD5E1] cursor-not-allowed"
                          }`}
                          title={
                            dragEnabled
                              ? "Drag to re-rank"
                              : "Clear filters and sort by priority desc to drag"
                          }
                        >
                          <GripVertical size={14} />
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          onClick={() => onEdit?.(s)}
                          disabled={!onEdit}
                          className="text-left w-full disabled:cursor-default"
                          title={onEdit ? "Click to edit" : undefined}
                        >
                        <div className="font-semibold text-[#0F172A] hover:text-primary">{s.name}</div>
                        {s.description && (
                          <div className="text-[11.5px] text-[#64748B] mt-0.5 line-clamp-1">
                            {s.description}
                          </div>
                        )}
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5">
                          <Icon size={14} />
                          <span className="font-semibold">{meta.label}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge tone={STATUS_TONE[s.status] || "slate"}>{s.status}</Badge>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
                        {s.priority ?? 0}
                      </td>
                      <td className="px-3 py-2.5 text-[12px] text-[#475569] whitespace-nowrap">
                        {fmtDate(s.from_date)} → {fmtDate(s.to_date)}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {s.status === "active" && (
                            <Btn
                              size="sm"
                              variant="ghost"
                              disabled={readOnly}
                              onClick={() => onStatusChange?.(s.id, "paused")}
                            >
                              <Pause size={13} />
                              Pause
                            </Btn>
                          )}
                          {s.status === "paused" && (
                            <Btn
                              size="sm"
                              variant="ghost"
                              disabled={readOnly}
                              onClick={() => onStatusChange?.(s.id, "active")}
                            >
                              <Play size={13} />
                              Resume
                            </Btn>
                          )}
                          {s.status !== "archived" && (
                            <Btn
                              size="sm"
                              variant="ghost"
                              disabled={readOnly}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Archive this scheme? It will no longer be evaluated at the till."
                                  )
                                ) {
                                  onStatusChange?.(s.id, "archived");
                                }
                              }}
                            >
                              <Archive size={13} />
                              Archive
                            </Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default ListTab;
